import { z } from 'zod';
import { isBefore, isAfter } from 'date-fns';

import {
  convertSendAtToDBValue,
  convertSendAtToFormValue
} from 'pages/dashboard/overview/events/schemas/commonUtils';
import { parseUTCDate } from 'utils/dateTimeUtils';

/**
 * Questionnaire Cutoff Modal Schema
 *
 * Form Input (browser timezone):
 * {
 *   hasQuestionnaireCutoff: boolean;
 *   questionnaireCutoffAt: Date | null;  // Date object in browser's timezone
 * }
 *
 * Form Output / DB Format (UTC):
 * {
 *   hasQuestionnaireCutoff: boolean;
 *   questionnaireCutoffAt: string | null;  // UTC string (e.g., '2024-01-15 10:30:00')
 * }
 *
 * The schema transforms Date → UTC string, then validates in UTC for simplicity.
 */

export const createQuestionnaireCutoffModalSchema = ({
  eventTimezone,
  registrationCutoffAt,
  datetimeEnd
}: {
  eventTimezone: string;
  registrationCutoffAt?: string | null;
  datetimeEnd?: string | null;
}) =>
  z
    .object({
      hasQuestionnaireCutoff: z.boolean(),
      questionnaireCutoffAt: z
        .date()
        .nullable()
        // Transform: Convert Date (browser timezone) → UTC string for DB
        .transform((date: Date | null): string | null =>
          date ? convertSendAtToDBValue(date, eventTimezone) : null
        )
        // Validation 1: Must be in the future
        .refine(
          (utcString) => {
            if (!utcString) {
              return true;
            }

            const questionnaireCutoffAtUTC = parseUTCDate(utcString);
            const now = new Date();

            return !isBefore(questionnaireCutoffAtUTC, now);
          },
          {
            message: 'Questionnaire cutoff must be in the future'
          }
        )
        // Validation 2: Must be at or after registration cutoff
        .refine(
          (utcString) => {
            if (!utcString || !registrationCutoffAt) {
              return true;
            }

            const questionnaireCutoffAtUTC = parseUTCDate(utcString);
            const regCutoffUTC = parseUTCDate(registrationCutoffAt);

            return !isBefore(questionnaireCutoffAtUTC, regCutoffUTC);
          },
          {
            message: "Questionnaire cutoff time can't be before registration cutoff time"
          }
        )
        // Validation 3: Must be at or before event end
        .refine(
          (utcString) => {
            if (!utcString || !datetimeEnd) {
              return true;
            }

            const questionnaireCutoffAtUTC = parseUTCDate(utcString);
            const eventEndUTC = parseUTCDate(datetimeEnd);

            return !isAfter(questionnaireCutoffAtUTC, eventEndUTC);
          },
          {
            message: "Questionnaire cutoff time can't be after the event ends"
          }
        )
    })
    // Object-level validation: Ensure date is provided when toggle is enabled
    .refine(
      (data) => {
        if (data.hasQuestionnaireCutoff && !data.questionnaireCutoffAt) {
          return false;
        }
        return true;
      },
      {
        message: 'Date and time are required',
        path: ['questionnaireCutoffAt']
      }
    );

// Form input type: What the form receives (Date objects in browser timezone)
export type QuestionnaireCutoffModalFormInput = z.input<
  ReturnType<typeof createQuestionnaireCutoffModalSchema>
>;

// Form output type: What gets sent to the backend (UTC strings)
export type QuestionnaireCutoffModalFormValues = z.infer<
  ReturnType<typeof createQuestionnaireCutoffModalSchema>
>;

/**
 * Convert database values to form input values
 * Transforms UTC strings from DB → Date objects in browser timezone for the form
 *
 * @param dbData - Data from database with UTC strings
 * @param eventTimezone - Event timezone for proper conversion
 * @returns Form input values with Date objects
 */
export const convertQuestionnaireCutoffDBToFormData = (
  dbData: { hasQuestionnaireCutoff: boolean; questionnaireCutoffAt?: string | null },
  eventTimezone: string
): QuestionnaireCutoffModalFormInput => {
  return {
    hasQuestionnaireCutoff: dbData.hasQuestionnaireCutoff,
    questionnaireCutoffAt: dbData.questionnaireCutoffAt
      ? convertSendAtToFormValue(dbData.questionnaireCutoffAt, eventTimezone)
      : null
  };
};
